const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

function decodeBase32(value: string): Uint8Array {
  const clean = value.toUpperCase().replace(/[\s=-]/g, "");
  if (!clean || /[^A-Z2-7]/.test(clean)) throw new Error("Invalid Base32 secret");
  let bits = "";
  for (const char of clean) bits += alphabet.indexOf(char).toString(2).padStart(5, "0");
  const bytes: number[] = [];
  for (let index = 0; index + 8 <= bits.length; index += 8) bytes.push(Number.parseInt(bits.slice(index, index + 8), 2));
  return Uint8Array.from(bytes);
}

export type TotpOptions = { digits?: 6 | 8; period?: number; algorithm?: "SHA-1" | "SHA-256" | "SHA-512" };

export async function totp(secret: string, timestamp = Date.now(), options: TotpOptions = {}): Promise<{ code: string; remaining: number }> {
  const digits = options.digits ?? 6;
  const period = Math.max(1, options.period ?? 30);
  const counter = Math.floor(timestamp / (period * 1000));
  const message = new ArrayBuffer(8);
  new DataView(message).setBigUint64(0, BigInt(counter));
  const key = await crypto.subtle.importKey("raw", new Uint8Array(decodeBase32(secret)), { name: "HMAC", hash: options.algorithm ?? "SHA-1" }, false, ["sign"]);
  const digest = new Uint8Array(await crypto.subtle.sign("HMAC", key, message));
  const offset = (digest.at(-1) ?? 0) & 0x0f;
  const binary = ((digest[offset] ?? 0) & 0x7f) << 24 |
    (digest[offset + 1] ?? 0) << 16 |
    (digest[offset + 2] ?? 0) << 8 |
    (digest[offset + 3] ?? 0);
  return { code: String(binary % 10 ** digits).padStart(digits, "0"), remaining: period - Math.floor(timestamp / 1000) % period };
}

export type OtpAuthDetails = {
  issuer: string;
  account: string;
  secret: string;
  digits: 6 | 8;
  period: number;
  algorithm: "SHA-1" | "SHA-256" | "SHA-512";
};

export function parseOtpAuthUri(value: string): OtpAuthDetails {
  const uri = new URL(value.trim());
  if (uri.protocol !== "otpauth:" || uri.hostname.toLowerCase() !== "totp") throw new Error("Only TOTP setup links are supported");
  const label = decodeURIComponent(uri.pathname.replace(/^\//, ""));
  const separator = label.indexOf(":");
  const labelIssuer = separator >= 0 ? label.slice(0, separator).trim() : "";
  const account = (separator >= 0 ? label.slice(separator + 1) : label).trim();
  const issuer = (uri.searchParams.get("issuer") ?? labelIssuer).trim();
  const secret = (uri.searchParams.get("secret") ?? "").trim().toUpperCase().replace(/[\s=-]/g, "");
  decodeBase32(secret);
  const digitsValue = Number(uri.searchParams.get("digits") ?? 6);
  if (digitsValue !== 6 && digitsValue !== 8) throw new Error("Only 6 or 8 digit codes are supported");
  const period = Number(uri.searchParams.get("period") ?? 30);
  if (!Number.isInteger(period) || period < 1 || period > 300) throw new Error("Invalid TOTP period");
  const algorithmValue = (uri.searchParams.get("algorithm") ?? "SHA1").toUpperCase().replace(/-/g, "");
  const algorithms = { SHA1: "SHA-1", SHA256: "SHA-256", SHA512: "SHA-512" } as const;
  const algorithm = algorithms[algorithmValue as keyof typeof algorithms];
  if (!algorithm) throw new Error("Unsupported TOTP algorithm");
  return { issuer, account, secret, digits: digitsValue, period, algorithm };
}

export function parseJwt(token: string): { header?: unknown; payload?: Record<string, unknown>; error?: string } {
  try {
    const [header, payload] = token.split(".");
    if (!header || !payload) throw new Error("JWT must contain three dot-separated segments");
    const decode = (part: string) => JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=")));
    return { header: decode(header), payload: decode(payload) as Record<string, unknown> };
  } catch (error) {
    return { error: error instanceof Error ? error.message : "Invalid JWT" };
  }
}

export function passwordStrength(value: string): { score: number; label: string } {
  let score = 0;
  if (value.length >= 12) score++;
  if (value.length >= 18) score++;
  if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score++;
  if (/\d/.test(value) && /[^A-Za-z0-9]/.test(value)) score++;
  return { score, label: ["Very weak", "Weak", "Fair", "Good", "Strong"][score] ?? "Very weak" };
}

export function generatePassword(length = 24): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*_-+=";
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, (byte) => chars[byte % chars.length]).join("");
}
